import React, { useState, useMemo, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
  SelectChangeEvent,
  Alert,
  Card,
  CardContent,
  Divider,
  Chip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { db } from '../services/firebase/config';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { isStripeAccountActive } from '../utils/stripeUtils';
import StripeActivationDialog from '../components/StripeActivationDialog';
import { doc, updateDoc } from 'firebase/firestore';

const CLOUDINARY_UPLOAD_PRESET = 'profile_photos_allure';
const CLOUDINARY_CLOUD_NAME = 'jasmine-jtr';

const AddDressScreen: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dressData, setDressData] = useState({
    title: '',
    description: '',
    originalPrice: '', // Prix d'achat de la robe
    rentalPrice: '', // Prix de location (fixe)
    deposit: '', // Caution définie par le propriétaire
    size: '',
    condition: '',
    location: '',
    marque: '',
    images: [] as File[],
    availablePeriods: [] as { start: string; end: string }[], // Périodes disponibles sélectionnées
    // Conditions de prêt
    cleaningConditions: '',
    depositConditions: '',
    deliveryMethod: '', // 'pickup', 'delivery', 'both'
    deliveryAddress: '',
    returnConditions: '',
    additionalConditions: '',
  });
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingPeriod, setSelectingPeriod] = useState(false);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [showStripeDialog, setShowStripeDialog] = useState(false);
  const [stripeActive, setStripeActive] = useState<boolean | null>(null);

  // Vérifier le statut Stripe au chargement
  useEffect(() => {
    const checkStripeStatus = async () => {
      if (user) {
        const active = await isStripeAccountActive(user.uid);
        setStripeActive(active);
      }
    };
    checkStripeStatus();
  }, [user]);

  // Le dialog StripeActivationDialog gère déjà l'activation complète
  // On passe juste une fonction vide car le prop onActivate est requis mais le dialog a sa propre logique
  const handleActivateStripe = () => {
    // Le dialog gère l'activation dans son propre handleActivate
  };

  // Générer les dates du mois actuel et des 3 mois suivants
  const generateCalendarDates = () => {
    const dates = [];
    const today = new Date();
    const endDate = new Date();
    endDate.setMonth(today.getMonth() + 3); // 3 mois à l'avance

    for (let d = new Date(today); d <= endDate; d.setDate(d.getDate() + 1)) {
      dates.push(new Date(d));
    }
    return dates;
  };

  const calendarDates = generateCalendarDates();

  // Vérifier si une date est dans une période sélectionnée
  const isDateInPeriod = (date: Date) => {
    const dateString = date.toISOString().split('T')[0];
    return dressData.availablePeriods.some(period => 
      dateString >= period.start && dateString <= period.end
    );
  };

  // Vérifier si une date est le début d'une période
  const isPeriodStart = (date: Date) => {
    const dateString = date.toISOString().split('T')[0];
    return dressData.availablePeriods.some(period => period.start === dateString);
  };

  // Vérifier si une date est la fin d'une période
  const isPeriodEnd = (date: Date) => {
    const dateString = date.toISOString().split('T')[0];
    return dressData.availablePeriods.some(period => period.end === dateString);
  };

  // Gérer la sélection des périodes
  const handleDateClick = (date: Date) => {
    const dateString = date.toISOString().split('T')[0];
    
    if (!selectingPeriod) {
      // Commencer une nouvelle sélection
      setTempStartDate(date);
      setSelectingPeriod(true);
    } else {
      // Finaliser la sélection
      if (tempStartDate) {
        const startDate = tempStartDate < date ? tempStartDate : date;
        const endDate = tempStartDate < date ? date : tempStartDate;
        
        const newPeriod = {
          start: startDate.toISOString().split('T')[0],
          end: endDate.toISOString().split('T')[0]
        };
        
        setDressData(prev => ({
          ...prev,
          availablePeriods: [...prev.availablePeriods, newPeriod]
        }));
      }
      
      setSelectingPeriod(false);
      setTempStartDate(null);
    }
  };

  // Supprimer une période
  const removePeriod = (index: number) => {
    setDressData(prev => ({
      ...prev,
      availablePeriods: prev.availablePeriods.filter((_, i) => i !== index)
    }));
  };

  // Gérer le changement de mois
  const handleMonthChange = (direction: 'prev' | 'next') => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setMonth(prev.getMonth() - 1);
      } else {
        newMonth.setMonth(prev.getMonth() + 1);
      }
      return newMonth;
    });
  };

  // Calculs automatiques basés sur le prix d'achat et l'état
  const calculations = useMemo(() => {
    const originalPrice = parseFloat(dressData.originalPrice) || 0;
    const condition = dressData.condition;
    
    if (originalPrice === 0) return null;

    // Facteurs de réduction selon l'état
    const conditionFactors = {
      'new': 1.0,        // Neuf : 100% du prix
      'like_new': 0.9,   // Comme neuf : 90% du prix
      'good': 0.8,       // Bon état : 80% du prix
      'fair': 0.7        // État correct : 70% du prix
    };

    const conditionFactor = conditionFactors[condition as keyof typeof conditionFactors] || 1.0;
    const adjustedValue = originalPrice * conditionFactor;
    
    // Suggestion de prix de location (12.5% de la valeur ajustée)
    const suggestedRentalPrice = Math.round(adjustedValue / 8); // 1/8 de la valeur ajustée
    
    // Caution (75% de la valeur ajustée)
    const deposit = Math.round(adjustedValue * 0.75);
    
    // Commission de la plateforme (12%)
    const commissionRate = 0.12;
    // Utiliser le prix de location choisi par l'utilisateur, sinon la suggestion
    const rentalPrice = parseFloat(dressData.rentalPrice) || suggestedRentalPrice;
    const commission = Math.round(rentalPrice * commissionRate);
    
    return {
      adjustedValue: Math.round(adjustedValue),
      suggestedRentalPrice,
      deposit,
      commission,
      commissionRate,
      rentalPrice // Prix effectif utilisé pour les calculs
    };
  }, [dressData.originalPrice, dressData.condition, dressData.rentalPrice]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setDressData(prev => ({
      ...prev,
      [name as string]: value
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setDressData(prev => ({
      ...prev,
      [name as string]: value
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setDressData(prev => ({
        ...prev,
        images: files
      }));
      setImagePreviews(files.map(file => URL.createObjectURL(file)));
    }
  };

  const handleRemoveImage = (idx: number) => {
    setDressData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== idx)
    }));
    setImagePreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const uploadToCloudinary = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
      { method: 'POST', body: formData }
    );
    const data = await response.json();
    if (!data.secure_url) throw new Error('Erreur Cloudinary');
    return data.secure_url;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!user) {
      setError('Utilisateur non connecté.');
      return;
    }

    // Vérifier le statut Stripe avant de permettre la publication
    const active = await isStripeAccountActive(user.uid);
    if (!active) {
      setShowStripeDialog(true);
      return;
    }

    if (!dressData.images || dressData.images.length === 0) {
      setError('Veuillez ajouter au moins une photo.');
      return;
    }
    
    setSubmitting(true);
    try {
      // Upload toutes les images sur Cloudinary
      const urls = await Promise.all(dressData.images.map(uploadToCloudinary));
      // Crée l'annonce dans Firestore
      await addDoc(collection(db, 'dresses'), {
        title: dressData.title,
        description: dressData.description,
        originalPrice: parseFloat(dressData.originalPrice),
        price: parseFloat(dressData.rentalPrice), // Prix de location
        deposit: parseFloat(dressData.deposit) || calculations?.deposit || 0,
        size: dressData.size,
        condition: dressData.condition,
        location: dressData.location,
        marque: dressData.marque,
        images: urls,
        availablePeriods: dressData.availablePeriods,
        // Conditions de prêt
        rentalConditions: {
          cleaning: dressData.cleaningConditions,
          deposit: dressData.depositConditions,
          deliveryMethod: dressData.deliveryMethod,
          deliveryAddress: dressData.deliveryAddress,
          return: dressData.returnConditions,
          additional: dressData.additionalConditions,
        },
        userId: user.uid,
        createdAt: Timestamp.now(),
      });
      navigate('/dashboard/profile');
    } catch (err) {
      setError('Erreur lors de la publication de l\'annonce.');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Proposer une robe
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Titre"
                name="title"
                value={dressData.title}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                multiline
                rows={4}
                label="Description"
                name="description"
                value={dressData.description}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                type="number"
                label="Prix d'achat de la robe (€)"
                name="originalPrice"
                value={dressData.originalPrice}
                onChange={handleChange}
                helperText="Prix que vous avez payé pour cette robe"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Prix de location (€)"
                name="rentalPrice"
                value={dressData.rentalPrice}
                onChange={handleChange}
                helperText={calculations ? `Suggestion: ${calculations.suggestedRentalPrice}€` : "Saisissez un prix de location"}
                placeholder={calculations ? calculations.suggestedRentalPrice.toString() : ""}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                type="number"
                label="Caution (€)"
                name="deposit"
                value={dressData.deposit}
                onChange={handleChange}
                inputProps={{ min: 0, step: 0.01 }}
                helperText={calculations ? `Suggestion: ${calculations.deposit}€ (75% de la valeur)` : "Montant de la caution"}
                placeholder={calculations ? calculations.deposit.toString() : ""}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Taille</InputLabel>
                <Select
                  name="size"
                  value={dressData.size}
                  onChange={handleSelectChange}
                  label="Taille"
                >
                  <MenuItem value="XS">XS</MenuItem>
                  <MenuItem value="S">S</MenuItem>
                  <MenuItem value="M">M</MenuItem>
                  <MenuItem value="L">L</MenuItem>
                  <MenuItem value="XL">XL</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>État de la robe</InputLabel>
                <Select
                  name="condition"
                  value={dressData.condition}
                  onChange={handleSelectChange}
                  label="État de la robe"
                >
                  <MenuItem value="new">Neuf (100% de la valeur)</MenuItem>
                  <MenuItem value="like_new">Comme neuf (90% de la valeur)</MenuItem>
                  <MenuItem value="good">Bon état (80% de la valeur)</MenuItem>
                  <MenuItem value="fair">État correct (70% de la valeur)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Localisation"
                name="location"
                value={dressData.location}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Marque"
                name="marque"
                value={dressData.marque}
                onChange={handleChange}
              />
            </Grid>

            {/* Calendrier de disponibilité */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: '#a5816d', mt: 2 }}>
                📅 Sélectionnez les périodes de disponibilité
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Cliquez sur une date de début, puis sur une date de fin pour créer une période de disponibilité
              </Typography>
              {selectingPeriod && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Sélection en cours... Cliquez sur la date de fin de la période
                </Alert>
              )}
              
              {/* Navigation du calendrier */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Button 
                  onClick={() => handleMonthChange('prev')}
                  disabled={currentMonth.getMonth() === new Date().getMonth() && currentMonth.getFullYear() === new Date().getFullYear()}
                >
                  ← Précédent
                </Button>
                <Typography variant="h6" sx={{ color: '#a5816d' }}>
                  {currentMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                </Typography>
                <Button onClick={() => handleMonthChange('next')}>
                  Suivant →
                </Button>
              </Box>

              {/* Grille du calendrier */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(7, 1fr)', 
                gap: 1, 
                mb: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                p: 2,
                backgroundColor: '#fafafa'
              }}>
                {/* En-têtes des jours */}
                {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map(day => (
                  <Typography key={day} variant="body2" align="center" sx={{ fontWeight: 'bold', color: '#a5816d' }}>
                    {day}
                  </Typography>
                ))}
                
                {/* Dates du mois */}
                {(() => {
                  const year = currentMonth.getFullYear();
                  const month = currentMonth.getMonth();
                  const firstDay = new Date(year, month, 1);
                  const lastDay = new Date(year, month + 1, 0);
                  const startDate = new Date(firstDay);
                  startDate.setDate(startDate.getDate() - firstDay.getDay());
                  
                  const dates = [];
                  for (let i = 0; i < 42; i++) {
                    const date = new Date(startDate);
                    date.setDate(startDate.getDate() + i);
                    dates.push(date);
                  }
                  
                  return dates.map((date, index) => {
                    const isCurrentMonth = date.getMonth() === month;
                    const isToday = date.toDateString() === new Date().toDateString();
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const isPast = date < today;
                    const isInPeriod = isDateInPeriod(date);
                    const isStart = isPeriodStart(date);
                    const isEnd = isPeriodEnd(date);
                    const isTempStart = tempStartDate && date.toDateString() === tempStartDate.toDateString();
                    
                    return (
                      <Button
                        key={index}
                        onClick={() => !isPast && handleDateClick(date)}
                        disabled={isPast}
                        sx={{
                          minWidth: 40,
                          height: 40,
                          fontSize: '0.875rem',
                          color: isCurrentMonth ? (isInPeriod ? 'white' : '#333') : '#ccc',
                          backgroundColor: isInPeriod 
                            ? (isStart || isEnd ? '#8B7355' : '#a5816d') 
                            : (isToday ? '#e3f2fd' : (isTempStart ? '#ffeb3b' : 'transparent')),
                          border: isToday ? '2px solid #1976d2' : (isTempStart ? '2px solid #ff9800' : 'none'),
                          borderRadius: isStart ? '50% 0 0 50%' : isEnd ? '0 50% 50% 0' : isInPeriod ? 0 : '50%',
                          '&:hover': {
                            backgroundColor: isPast ? 'transparent' : (isInPeriod ? '#8B7355' : '#f0f0f0')
                          },
                          opacity: isPast ? 0.3 : 1,
                          cursor: isPast ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {date.getDate()}
                      </Button>
                    );
                  });
                })()}
              </Box>

              {/* Résumé des périodes sélectionnées */}
              {dressData.availablePeriods.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Périodes sélectionnées ({dressData.availablePeriods.length}):
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {dressData.availablePeriods.map((period, index) => (
                      <Chip
                        key={index}
                        label={`${new Date(period.start).toLocaleDateString('fr-FR')} - ${new Date(period.end).toLocaleDateString('fr-FR')}`}
                        onDelete={() => removePeriod(index)}
                        size="small"
                        sx={{ backgroundColor: '#a5816d', color: 'white' }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Grid>

            {/* Affichage des calculs automatiques */}
            {calculations && (
              <Grid item xs={12}>
                <Card sx={{ backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: '#a5816d', fontWeight: 'bold' }}>
                      💰 Calculs automatiques
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Valeur ajustée selon l'état:
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                          {calculations.adjustedValue}€
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Prix de location {dressData.rentalPrice ? 'choisi' : 'suggéré'}:
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                          {calculations.rentalPrice}€
                        </Typography>
                      </Grid>
            <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Caution suggérée (75% de la valeur):
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#f57c00', fontWeight: 'bold' }}>
                          {calculations.deposit}€
                        </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">
                          Commission Allure (12%):
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#a5816d', fontWeight: 'bold' }}>
                          {calculations.commission}€/location
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Basée sur {calculations.rentalPrice}€
                        </Typography>
                      </Grid>
                    </Grid>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      💡 <strong>Note:</strong> La caution ne sera pas payée mais bloquée sur le compte du payeur. Nous nous réservons le droit de prélever l'argent sur le compte en cas de problème (dommages, perte, etc.).
                    </Typography>
                  </CardContent>
                </Card>
            </Grid>
            )}

            {/* Conditions de prêt */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: '#a5816d', mt: 2 }}>
                📋 Conditions de prêt
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Définissez les conditions spécifiques pour la location de votre robe
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                multiline
                rows={3}
                label="Conditions de nettoyage"
                name="cleaningConditions"
                value={dressData.cleaningConditions}
                onChange={handleChange}
                placeholder="Ex: La robe doit être retournée propre et repassée. En cas de taches, des frais de nettoyage de 15€ seront appliqués."
                helperText="Décrivez vos attentes concernant le nettoyage de la robe"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                multiline
                rows={2}
                label="Conditions de caution"
                name="depositConditions"
                value={dressData.depositConditions}
                onChange={handleChange}
                placeholder="Ex: La caution sera bloquée sur votre compte et débloquée 48h après le retour en bon état."
                helperText="Expliquez comment la caution sera gérée"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Mode de remise</InputLabel>
                <Select
                  name="deliveryMethod"
                  value={dressData.deliveryMethod}
                  onChange={handleSelectChange}
                  label="Mode de remise"
                >
                  <MenuItem value="pickup">Remise en main propre uniquement</MenuItem>
                  <MenuItem value="delivery">Livraison uniquement</MenuItem>
                  <MenuItem value="both">Remise en main propre ou livraison</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {dressData.deliveryMethod !== 'pickup' && (
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Adresse de livraison/retrait"
                  name="deliveryAddress"
                  value={dressData.deliveryAddress}
                  onChange={handleChange}
                  placeholder="Ex: 123 Rue de la Paix, 75001 Paris (Métro Châtelet)"
                  helperText="Adresse complète où la robe peut être récupérée ou livrée"
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                multiline
                rows={2}
                label="Conditions de retour"
                name="returnConditions"
                value={dressData.returnConditions}
                onChange={handleChange}
                placeholder="Ex: Retour dans les 24h suivant la fin de location. Retard = 10€/jour supplémentaire."
                helperText="Délais et conditions pour le retour de la robe"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Conditions supplémentaires"
                name="additionalConditions"
                value={dressData.additionalConditions}
                onChange={handleChange}
                placeholder="Ex: Interdiction de fumer, de manger ou de boire avec la robe. Photos autorisées uniquement."
                helperText="Toute autre condition importante pour la location"
              />
            </Grid>

            {/* Ajout de photos multiples obligatoire */}
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
              >
                Ajouter des photos
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                />
              </Button>
              {imagePreviews.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                  {imagePreviews.map((src, idx) => (
                    <Box key={idx} sx={{ position: 'relative', width: 100, height: 100, border: '1px solid #eee', borderRadius: 2, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src={src} alt={`Aperçu ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <Button
                        size="small"
                        onClick={() => handleRemoveImage(idx)}
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          minWidth: 0,
                          padding: 0.5,
                          background: 'rgba(255,255,255,0.7)',
                          borderRadius: '50%',
                          zIndex: 2,
                          color: 'red',
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </Button>
                    </Box>
                  ))}
                </Box>
              )}
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
              )}
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                disabled={
                  !dressData.images || dressData.images.length === 0 || 
                  submitting || !dressData.originalPrice || !dressData.rentalPrice || 
                  dressData.availablePeriods.length === 0 ||
                  !dressData.cleaningConditions || !dressData.depositConditions ||
                  !dressData.deliveryMethod || !dressData.returnConditions ||
                  (dressData.deliveryMethod !== 'pickup' && !dressData.deliveryAddress)
                }
              >
                {submitting ? 'Publication en cours...' : "Publier l'annonce"}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Dialog d'activation Stripe obligatoire */}
      <StripeActivationDialog
        open={showStripeDialog}
        onClose={() => setShowStripeDialog(false)}
        onActivate={handleActivateStripe}
        required={true}
      />
    </Container>
  );
};

export default AddDressScreen; 